"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import RevealOnScroll from "@/components/atoms/RevealOnScroll";
// [RUDDERSTACK] widget_impression + cta_click are now added centrally by NewPage
// (TrackedWidget + CtaClickDelegate). The CTA below carries data-cta-name so the
// delegate keeps the exact "download_brochure" name; no tracking code lives here.
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";
import {
  LEAD_DROP_FORM_MOBILE_CTA_VARIANTS,
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_FORM_VARIANTS,
} from "../../../constants";

/**
 * Banner 10 — Hero Banner (W3).
 *
 * Program-page hero: a value-proposition content column (left) paired with the
 * university's configured inline lead form (right on desktop, CTA on mobile —
 * embedded the same way as About 2). Typography is mapped 1:1 from the Figma
 * "Typography System · 4 Breakpoints" file across xs / sm(1080) / md(1230) /
 * lg(1430) / xl(1750). The mobile floating "Admissions open" badge in the mock
 * is intentionally omitted.
 *
 * Anatomy (PRD 444:2508): widget background image, optional breadcrumbs,
 * eyebrow badges (1–3), heading + sub heading + supporting line, paragraph
 * (3 bullets OR 3-line text), optional collaboration image, CTA (opens lead
 * drawer) and two trust builders (alumni avatars, star rating).
 */

const MAX_BADGES = 3;
const MAX_BULLETS = 3;
const MAX_AVATARS = 6;

type Row = Record<string, any>;

const isOn = (v: unknown) => v === true || v === 1 || v === "1";

const activeSorted = (rows: Row[] | undefined, statusKey = "status", priorityKey = "priority") =>
  Array.isArray(rows)
    ? [...rows]
        .filter((r) => isOn(r?.[statusKey]))
        .sort((a, b) => Number(a?.[priorityKey] ?? 0) - Number(b?.[priorityKey] ?? 0))
    : [];

/** Default leading bullet icon (Vuesax bold tick-circle). */
const TickIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm4.78 7.7-5.67 5.67a.75.75 0 0 1-1.06 0l-2.83-2.83a.75.75 0 1 1 1.06-1.06l2.3 2.3 5.14-5.14a.75.75 0 1 1 1.06 1.06Z"
      fill="currentColor"
    />
  </svg>
);

const StarIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 17.27 6.18 21l1.64-7.03L2 9.24l7.19-.61L12 2l2.81 6.63 7.19.61-5.82 4.73L17.82 21 12 17.27Z" />
  </svg>
);

const DownloadArrow = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 5v14" />
    <path d="m19 12-7 7-7-7" />
  </svg>
);

const Banner10 = (props: any) => {
  const { data: sectionData } = props;
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);

  // Layout switches to two columns at the sm (1080px) breakpoint; below that the
  // inline form collapses to a CTA — mirrors About 2's form-embedding approach.
  const isMobile = useIsMobile(1080);

  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );
  const leadDropFormRow = useSelector((state: RootState) => {
    const blob = state.leaddropform.lead_drop_form as
      | { formData?: Record<string, unknown> }
      | null
      | undefined;
    return blob?.formData ?? null;
  });

  // Program brochure URL (same source as Banner2 / ProgramStructure). Passed to
  // the DOWNLOAD-variant lead popup so it auto-downloads once the lead is
  // submitted. Empty on pages with no globally-selected course — that is fine:
  // it is only the fallback, and the popup resolves the brochure of the course
  // the lead picks in the form (see resolveBrochureLink in @/lib/brochure).
  const brochureUrl = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );

  const LeaddropFormComponent = useMemo(
    () => (leaddropFormVariant ? LEAD_DROP_FORM_SECTION_VARIANTS[leaddropFormVariant] ?? null : null),
    [leaddropFormVariant],
  );
  const LeaddropFormMobileCtaComponent = useMemo(
    () => (leaddropFormVariant ? LEAD_DROP_FORM_MOBILE_CTA_VARIANTS[leaddropFormVariant] ?? null : null),
    [leaddropFormVariant],
  );

  const {
    metadata = [], // eyebrow badges (max 3)
    metadata_1 = [], // paragraph bullets (exactly 3)
    metadata_2 = [], // breadcrumbs (optional)
    metadata_3 = [], // alumni avatars

    background_image = "",
    background_image_mobile = "",
    background_color = "#50040B",

    // Per-view breadcrumb flags. `show_breadcrumbs` is the legacy shared flag and
    // stays the fallback for rows saved before the split — read it with `??`, not
    // `||`, so an explicit "off" is not overridden.
    show_breadcrumbs = false,
    show_breadcrumbs_desktop,
    show_breadcrumbs_mobile,

    eyebrow_badge_bg_color = "rgba(255,255,255,0.14)",
    // Mobile-only opt-out for the eyebrow badge strip. NULL/undefined (rows saved
    // before the toggle existed) means "show" — badges have always rendered on
    // mobile, so absence must keep that behaviour. Desktop is not gated.
    show_eyebrow_badges_mobile,

    title_1 = "",
    title_1_color = "#F5F5F5",
    // Mobile-only opt-outs for the heading (title_1) and sub heading (title_2).
    // NULL/undefined (rows saved before the toggles) means "show". Desktop is
    // not gated.
    show_title_1_mobile,
    title_2 = "",
    title_2_color = "#F5F5F5",
    show_title_2_mobile,
    subtitle = "",
    subtitle_color = "rgba(255,255,255,0.8)",
    // Mobile-only opt-out for the supporting line (subtitle); NULL = show.
    show_subtitle_mobile,

    // Mobile-only creative (4:5). The full creative — headline, artwork,
    // supporting text, corner treatment — is baked into the uploaded image;
    // nothing inside it is coded. Mandatory on mobile (enforced by the admin
    // form), absent from desktop entirely. Shadow: show/hide + colour only —
    // geometry is fixed here on purpose.
    mobile_creative_image = "",
    show_mobile_creative_shadow = false,
    mobile_creative_shadow_color = "rgba(0,0,0,0.35)",

    paragraph_variant = "bullets",
    // Mobile-only opt-out for the paragraph block (bullets AND text variants).
    // NULL/undefined (rows saved before the toggle) means "show" — the block has
    // always rendered on mobile. Desktop is not gated.
    show_paragraph_mobile,
    paragraph_text = "",
    paragraph_color = "#FFFFFF",
    bullet_icon = "",

    collaboration_image = "",
    collaboration_image_mobile = "",

    download_brochure_text = "Download Brochure",
    button_color = "#FFFFFF",
    cta_text_color = "#222222",
    explore_cta_url = "",
    // "download" = brochure lead form (historical behaviour), "apply" =
    // registration lead form. A non-empty explore_cta_url overrides both.
    cta_action = "download",

    // Trust Builders: legacy shared flags plus per-view splits. The shared flag
    // is the fallback (`??`, not `||`) for rows saved before the split, so an
    // explicit off survives and old rows keep their behaviour on both views.
    show_learners = true,
    show_learners_desktop,
    show_learners_mobile,
    learners_description = "Join 10k+ alumni",
    learners_description_color = "#FFFFFF",

    show_rating = true,
    show_rating_desktop,
    show_rating_mobile,
    rating_number = "4/5",
    rating_number_color = "#FFFFFF",
    rating_text = "(7.8K ratings)",
    rating_color = "rgba(255,255,255,0.5)",

    enable_lead_form = true,
    enable_animation,
    status = true,
  } = sectionData || {};

  const isAnimationEnabled = isOn(enable_animation);

  const badges = useMemo(() => activeSorted(metadata).slice(0, MAX_BADGES), [metadata]);
  const bullets = useMemo(() => activeSorted(metadata_1).slice(0, MAX_BULLETS), [metadata_1]);
  const avatars = useMemo(() => activeSorted(metadata_3).slice(0, MAX_AVATARS), [metadata_3]);
  const crumbs = useMemo(
    () => activeSorted(metadata_2, "meta_status", "meta_priority"),
    [metadata_2],
  );

  const filledStars = useMemo(() => {
    const value = parseFloat(String(rating_number).split("/")[0]);
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(5, Math.round(value)));
  }, [rating_number]);

  const showCrumbsDesktop = isOn(show_breadcrumbs_desktop ?? show_breadcrumbs);
  const showCrumbsMobile = isOn(show_breadcrumbs_mobile ?? show_breadcrumbs);
  const showBadgesMobile = isOn(show_eyebrow_badges_mobile ?? true);
  const showTitle1Mobile = isOn(show_title_1_mobile ?? true);
  const showTitle2Mobile = isOn(show_title_2_mobile ?? true);
  const showSubtitleMobile = isOn(show_subtitle_mobile ?? true);
  const showParagraphMobile = isOn(show_paragraph_mobile ?? true);
  const showCreativeShadow = isOn(show_mobile_creative_shadow);


  const mobileHasBadges = badges.length > 0 && showBadgesMobile;
  const mobileHasHeadingText =
    (Boolean(title_1) && showTitle1Mobile) ||
    (Boolean(title_2) && showTitle2Mobile) ||
    (Boolean(subtitle) && showSubtitleMobile);
  const mobileHasHeadingGroup = mobileHasBadges || mobileHasHeadingText;

  const showLearnersDesktop = isOn(show_learners_desktop ?? show_learners);
  const showLearnersMobile = isOn(show_learners_mobile ?? show_learners);
  const showRatingDesktop = isOn(show_rating_desktop ?? show_rating);
  const showRatingMobile = isOn(show_rating_mobile ?? show_rating);

  const showLeadForm = isOn(enable_lead_form);
  const showBullets = String(paragraph_variant) !== "paragraph";
  const isApplyCta = String(cta_action).toLowerCase() === "apply";

  if (status === false || status === 0 || status === "0") return null;

  const handleCtaClick = () => {
    const link = String(explore_cta_url || "").trim();
    if (link) {
      if (link.startsWith("#")) {
        document.querySelector(link)?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.open(link, /^https?:\/\//i.test(link) ? "_blank" : "_self");
      }
      return;
    }
    // Apply opens the registration form, which has no brochure to hand off.
    dispatch(
      handleOpenLeaddrop(
        isApplyCta
          ? { variant: LEAD_DROP_VARIANTS.REGISTER }
          : {
              variant: LEAD_DROP_VARIANTS.DOWNLOAD,
              downloadLink: brochureUrl,
            },
      ),
    );
  };

  /* ── Reusable presentational pieces ─────────────────────────────────────── */

  const renderCrumbLabel = (c: Row, isLast: boolean) => {
    const label = String(c.meta_label ?? "").trim();
    const href = String(c.meta_url ?? "").trim();
    const color = isLast ? "#FFFFFF" : "rgba(255,255,255,0.5)";

    // Last crumb (current page) or no URL → plain text.
    if (isLast || !href) {
      return (
        <span aria-current={isLast ? "page" : undefined} style={{ color }}>
          {label}
        </span>
      );
    }
    // External URL → new tab; internal path → client-side navigation.
    if (/^https?:\/\//i.test(href)) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline-offset-2 hover:underline"
          style={{ color }}
        >
          {label}
        </a>
      );
    }
    return (
      <Link
        href={href.startsWith("/") ? href : `/${href}`}
        className="underline-offset-2 hover:underline"
        style={{ color }}
      >
        {label}
      </Link>
    );
  };

  const Breadcrumbs = () => {
    if (crumbs.length === 0 || (!showCrumbsDesktop && !showCrumbsMobile)) return null;

    return (
      <nav
        aria-label="Breadcrumb"
        className={cn(
          "order-1 w-full flex-wrap items-center gap-x-1 font-poppins font-medium leading-[1.5] sm:order-none",
          "justify-center sm:justify-start",
          "text-[10px] sm:text-[12px] lg:text-[13px] xl:text-[14px]",
          // Per-view visibility is done in CSS, not with useIsMobile: the flags
          // must apply on the very first (server) render, and a JS-resolved
          // breakpoint would pop the strip in a frame later — the same CLS trap
          // the mobile lead-form branch below documents. `sm` = 1080px (@theme),
          // matching the desktop/mobile split used by the rest of the widget.
          // A display:none nav also collapses out of the column's gap, so hiding
          // it leaves no empty band.
          showCrumbsMobile ? "flex" : "hidden",
          showCrumbsDesktop ? "sm:flex" : "sm:hidden",
        )}
      >
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <span key={`${c.id ?? c.meta_label}-${i}`} className="inline-flex items-center gap-x-1">
              {i > 0 && <span style={{ color: "rgba(255,255,255,0.5)" }}>/</span>}
              {renderCrumbLabel(c, isLast)}
            </span>
          );
        })}
      </nav>
    );
  };

  const TrustBuilders = () => {
    if (
      !showLearnersDesktop &&
      !showLearnersMobile &&
      !showRatingDesktop &&
      !showRatingMobile
    )
      return null;
    return (
      <div
        className={cn(
          "flex-col items-center gap-[12px]",
          "sm:flex-1 sm:flex-row sm:items-center sm:justify-between sm:gap-[10px]",
          // Per-view visibility in CSS (not useIsMobile) so the flags hold on
          // the first server render — same CLS reasoning as the breadcrumb
          // strip. The container itself collapses when neither builder shows in
          // a view, so no empty gap band is left. `sm` = 1080px (@theme).
          showLearnersMobile || showRatingMobile ? "flex" : "hidden",
          showLearnersDesktop || showRatingDesktop ? "sm:flex" : "sm:hidden",
        )}
      >
        {(showLearnersDesktop || showLearnersMobile) && (
          <div
            className={cn(
              "items-center gap-[10px]",
              showLearnersMobile ? "flex" : "hidden",
              showLearnersDesktop ? "sm:flex" : "sm:hidden",
            )}
          >
            {avatars.length > 0 && (
              <div className="flex items-center">
                {avatars.map((a, i) => (
                  <div
                    key={a.id ?? i}
                    className={cn(
                      "relative shrink-0 overflow-hidden rounded-full ring-2 ring-white/80",
                      "size-[20px] xl:size-[24px]",
                      i > 0 && "-ml-2",
                    )}
                  >
                    {a.image && (
                      // 20–24px avatar; without `sizes` next/image defaults to
                      // 100vw and ships a ~250px source (PSI: 250×250 for a
                      // 35×35 slot). Pin it to the rendered size.
                      <Image src={a.image} alt="" fill sizes="24px" className="object-cover" />
                    )}
                  </div>
                ))}
              </div>
            )}
            {learners_description && (
              <p
                className={cn(
                  "whitespace-nowrap font-poppins font-medium leading-[1.5]",
                  "text-[13px] tracking-[0.13px] sm:text-[12px] sm:tracking-[0.12px]",
                  "lg:text-[13px] lg:tracking-[0.13px] xl:text-[14px] xl:tracking-[0.14px]",
                )}
                style={{ color: learners_description_color }}
              >
                {learners_description}
              </p>
            )}
          </div>
        )}

        {/* The divider only ever renders on desktop, so only the desktop flags gate it. */}
        {showLearnersDesktop && showRatingDesktop && (
          <div className="hidden h-[30px] w-px shrink-0 bg-white/30 sm:block" />
        )}

        {(showRatingDesktop || showRatingMobile) && (
          <div
            className={cn(
              "items-center gap-[10px]",
              showRatingMobile ? "flex" : "hidden",
              showRatingDesktop ? "sm:flex" : "sm:hidden",
            )}
          >
            <div className="flex items-center gap-[4px] sm:gap-[2px] md:gap-[4px]">
              {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon
                  key={i}
                  className={cn(
                    "size-[20px] sm:size-[14px] md:size-[16px] lg:size-[20px] xl:size-[24px]",
                    i < filledStars ? "text-[#F5A623]" : "text-[#F5A623] opacity-30",
                  )}
                />
              ))}
            </div>
            <p
              className={cn(
                "whitespace-nowrap font-poppins font-medium leading-[1.5]",
                "text-[13px] sm:text-[12px] lg:text-[13px] xl:text-[14px]",
              )}
            >
              <span style={{ color: rating_number_color }}>{rating_number}</span>
              {rating_text && (
                <span style={{ color: rating_color }}> {rating_text}</span>
              )}
            </p>
          </div>
        )}
      </div>
    );
  };

  const Cta = () =>
    download_brochure_text ? (
      <button
        type="button"
        onClick={handleCtaClick}
        data-cta-name={isApplyCta ? "apply_now" : "download_brochure"}
        className={cn(
          "flex w-full items-center justify-center gap-[10px] rounded-[8px] sm:w-auto sm:gap-[8px]",
          "px-[24px] py-[16px] sm:px-[16px] sm:py-[12px] md:px-[20px] lg:px-[24px] lg:py-[16px]",
          "font-poppins font-semibold leading-[1.4] sm:font-normal md:font-semibold",
          "text-[16px] sm:text-[14px] lg:text-[16px] transition-opacity hover:opacity-90",
          "cursor-pointer"
        )}
        style={{ backgroundColor: button_color, color: cta_text_color }}
      >
        <span className="whitespace-nowrap">{download_brochure_text}</span>
        {/* The download arrow only makes sense for the brochure action. */}
        {!isApplyCta && (
          <DownloadArrow className="size-[24px] shrink-0 sm:size-[20px] md:size-[24px]" />
        )}
      </button>
    ) : null;

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: background_color }}
    >
      {/* Full-bleed widget background (overlay baked into the uploaded image).
          Single <picture> so the browser downloads ONLY the source that matches
          the viewport. Previously the desktop and mobile backgrounds were two
          separate <img>s that BOTH carried fetchPriority="high" — and a CSS
          `hidden` <img> is still fetched — so a phone paid for two full-bleed
          hero downloads at the highest priority, starving the actual LCP image
          (this banner's background is the page's LCP element). The chosen
          source keeps the high fetch priority. sm = 1080px (see @theme). */}
      {(background_image || background_image_mobile) && (
        <picture>
          {background_image && background_image_mobile && (
            <source media="(min-width: 1080px)" srcSet={background_image} />
          )}
          <img
            src={background_image_mobile || background_image}
            alt=""
            aria-hidden
            fetchPriority="high"
            className="pointer-events-none absolute inset-0 size-full object-cover"
          />
        </picture>
      )}

      <div
        className={cn(
          "relative z-10 flex w-full flex-col items-center gap-[40px] px-[16px] py-[40px] text-center",
          // Figma component reference: 80px horizontal padding from sm up; mobile keeps its existing
          // gutter. No max-width — content spans the full section width (edge-to-edge within the 80px).
          "sm:flex-row sm:items-center sm:gap-[40px] sm:px-[80px] sm:text-left",
          "md:gap-[60px]",
          "lg:gap-[120px]",
          "xl:py-[60px]",
        )}
      >
        {/* ── Left: value-proposition content column ─────────────────────────
            MOBILE sequence (product ask): Breadcrumbs → Eyebrow badges →
            Heading → Sub heading → Creative → CTA → Trust Builders →
            Paragraph → Collaboration → Lead form. The DOM keeps the DESKTOP
            order; mobile is re-sequenced with `order-*` on the column children,
            reset via `sm:order-none` so desktop stays exactly as before.
            (Inside the CTA/Trust row, CTA comes first on both views.) */}
        <div
          className={cn(
            "flex w-full min-w-0 flex-col items-center gap-[40px] sm:flex-1 sm:items-start sm:gap-[20px] md:gap-[40px]",
          )}
        >
          <Breadcrumbs />

          {/* Eyebrow badges + heading group */}
          <div
            className={cn(
              "order-2 flex w-full flex-col items-center gap-[20px] sm:order-none sm:items-start sm:gap-[24px]",
              // Nothing inside shows on mobile — drop the wrapper as a flex item
              // so it stops claiming the column's 40px gap.
              !mobileHasHeadingGroup && "max-sm:hidden",
            )}
          >
            {badges.length > 0 && (
              <div
                className={cn(
                  "flex-wrap items-center justify-center gap-[6px] sm:justify-start sm:gap-[12px]",
                  // Mobile-only opt-out, gated in CSS for the same reason as the
                  // breadcrumb strip: the flag has to hold on the first (server)
                  // render, and display:none collapses out of the group's gap.
                  showBadgesMobile ? "flex" : "hidden sm:flex",
                )}
              >
                {badges.map((b, i) => (
                  <div
                    key={b.id ?? i}
                    className="flex items-center justify-center gap-[7px] rounded-[20px] px-[12px] py-[6px] backdrop-blur-[42px]"
                    style={{ backgroundColor: eyebrow_badge_bg_color }}
                  >
                    {b.logo && (
                      <div className="relative h-[14px] w-[13px] shrink-0 sm:h-[20px] sm:w-[18px]">
                        <Image src={b.logo} alt="" fill sizes="20px" className="object-contain" />
                      </div>
                    )}
                    <span
                      className={cn(
                        "whitespace-nowrap font-poppins font-medium uppercase leading-[1.4] text-white",
                        "text-[9px] tracking-[0.72px] sm:text-[12px] sm:tracking-[0.96px]",
                        "lg:text-[13px] lg:tracking-[1.04px] xl:text-[14px] xl:tracking-[1.12px]",
                      )}
                    >
                      {b.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div
              className={cn(
                "flex w-full flex-col items-center gap-[6px] sm:items-start",
                // All three lines hidden on mobile — same reasoning, this time
                // for the group's own 20px gap below the badges.
                !mobileHasHeadingText && "max-sm:hidden",
              )}
            >
              {/* Heading / sub heading mobile opt-outs: `max-sm:hidden` so the
                  desktop display is untouched — same reasoning as the paragraph
                  block, CSS-gated for first-render correctness. */}
              {title_1 && (
                <h1
                  className={cn(
                    !showTitle1Mobile && "max-sm:hidden",
                    "w-full font-poppins font-semibold leading-[1.3] sm:font-medium lg:font-semibold xl:leading-[1.25]",
                    "text-[20px] tracking-[-0.2px] sm:text-[22px] sm:tracking-[-0.22px]",
                    "md:text-[24px] md:tracking-[-0.24px] lg:text-[28px] lg:tracking-[-0.28px]",
                    "xl:text-[32px] xl:tracking-[-0.32px]",
                  )}
                  style={{ color: title_1_color }}
                >
                  {title_1}
                </h1>
              )}
              {title_2 && (
                <h2
                  className={cn(
                    !showTitle2Mobile && "max-sm:hidden",
                    "w-full font-poppins font-semibold leading-[1.15] sm:leading-[1.2] lg:leading-[1.15]",
                    "text-[30px] tracking-[-0.6px] sm:text-[36px] sm:tracking-[-0.72px]",
                    "md:text-[40px] md:tracking-[-0.8px] lg:text-[48px] lg:tracking-[-0.96px]",
                    "xl:text-[56px] xl:tracking-[-1.12px]",
                  )}
                  style={{ color: title_2_color }}
                >
                  {title_2}
                </h2>
              )}
              {subtitle && (
                <p
                  className={cn(
                    !showSubtitleMobile && "max-sm:hidden",
                    "w-full font-poppins font-normal leading-[1.55]",
                    "text-[13px] tracking-[0.065px] sm:text-[14px] sm:tracking-[0.07px]",
                    "lg:text-[15px] lg:tracking-[0.075px] xl:text-[16px] xl:tracking-[0.08px]",
                  )}
                  style={{ color: subtitle_color }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Mobile-only creative (4:5) — below the sub-heading, above the Trust
              Builders. The whole creative is baked into the upload: no coded
              text, no coded corner rounding (the user manages corners in the
              image itself). Gated with `sm:hidden` (CSS, not useIsMobile) so it
              holds on the first server render — same CLS reasoning as the lead
              form below. Shadow is a fixed geometry; the admin controls only
              show/hide and colour. */}
          {mobile_creative_image && (
            <div
              className="order-3 relative aspect-[4/5] w-full sm:hidden"
              style={
                showCreativeShadow
                  ? { boxShadow: `0 8px 24px 0 ${mobile_creative_shadow_color}` }
                  : undefined
              }
            >
              <Image
                src={mobile_creative_image}
                alt=""
                fill
                sizes="100vw"
                className="object-cover"
              />
            </div>
          )}

          {/* Paragraph — bullets (exactly 3) or 3-line text */}
          {/* Paragraph mobile opt-out: `max-sm:hidden` (not `hidden sm:*`) so
              each branch keeps its own display above sm — re-declaring it would
              break the text branch's line-clamp (-webkit-box). CSS-gated for
              first-render correctness, same as the other per-view flags. */}
          {showBullets
            ? bullets.length > 0 && (
                <div
                  className={cn(
                    "order-5 flex w-full flex-col items-start gap-[10px] sm:order-none",
                    !showParagraphMobile && "max-sm:hidden",
                  )}
                >
                  {bullets.map((bl, i) => (
                    <RevealOnScroll
                      key={bl.id ?? i}
                      className="w-full"
                      y={20}
                      delay={i * 80}
                      enableAnimation={isAnimationEnabled}
                    >
                      <div className="flex items-center gap-[11px]">
                        {bl.icon || bullet_icon ? (
                          <div className="relative size-[18px] shrink-0 sm:size-[22px]">
                            <Image
                              src={bl.icon || bullet_icon}
                              alt=""
                              fill
                              sizes="22px"
                              className="object-contain"
                            />
                          </div>
                        ) : (
                          <TickIcon
                            className="size-[18px] shrink-0 text-white sm:size-[22px]"
                          />
                        )}
                        <p
                          className={cn(
                            "font-poppins font-normal leading-[1.55]",
                            "text-[12px] sm:text-[14px] lg:text-[15px] xl:text-[16px]",
                          )}
                          style={{ color: paragraph_color }}
                        >
                          {bl.text}
                        </p>
                      </div>
                    </RevealOnScroll>
                  ))}
                </div>
              )
            : paragraph_text && (
                <p
                  className={cn(
                    "order-5 line-clamp-3 w-full font-poppins font-normal leading-[1.55] sm:order-none",
                    !showParagraphMobile && "max-sm:hidden",
                    "text-[12px] sm:text-[14px] lg:text-[15px] xl:text-[16px]",
                  )}
                  style={{ color: paragraph_color }}
                >
                  {paragraph_text}
                </p>
              )}

          {/* Collaboration (single image; separate desktop / mobile uploads) */}
          {collaboration_image && (
            <div className="relative hidden aspect-[1460/200] w-full sm:block">
              <Image
                src={collaboration_image}
                alt=""
                fill
                className="object-contain object-left"
              />
            </div>
          )}
          {collaboration_image_mobile && (
            <div className="order-6 relative block aspect-[327/120] w-full sm:hidden">
              <Image
                src={collaboration_image_mobile}
                alt=""
                fill
                className="object-contain"
              />
            </div>
          )}

          {/* CTA + Trust builders — order-4 on mobile: directly below the
              creative, above the paragraph (see the column comment). */}
          <div
            className={cn(
              "order-4 flex w-full flex-col items-center gap-[20px] sm:order-none",
              "sm:flex-row sm:items-center sm:gap-[20px] lg:gap-[32px]",
            )}
          >
            {/* CTA first, Trust Builders after — same order on both views, so
                no per-view `order` swap needed any more. */}
            <div className="w-full sm:w-auto">
              <Cta />
            </div>
            <div className="w-full sm:flex-1">
              <TrustBuilders />
            </div>
          </div>

          {/* Mobile: inline form collapses to the configured lead CTA — LAST in
              the column (order-7), below the collaboration image (product ask).
              Gated on `!== false` (i.e. render while useIsMobile is still
              resolving to null) and hidden on desktop via `sm:hidden`, so the
              mobile branch is part of the very first render instead of being
              injected a frame later once useIsMobile's effect flips null→true —
              that late injection was this banner's layout-shift (CLS) culprit,
              and even in last position it would still shove the widgets BELOW
              this banner down. The desktop right-column form below still mounts
              only when isMobile is resolved to `false`, so the two branches
              never mount together. */}
          {showLeadForm && isMobile !== false && LeaddropFormMobileCtaComponent && (
            <div
              className={cn(
                "order-7 flex w-full flex-col items-center sm:hidden",
                // Optimistic height: form 9's mobile slot renders a FULL inline
                // form that loads a frame late — reserve ~its rendered height so
                // it doesn't shove the content below it down (CLS). Tune if the
                // card height changes. Button-style mobile CTAs keep their
                // natural height (no reservation).
                leaddropFormVariant === LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_9 &&
                  "min-h-[520px]",
              )}
            >
              <LeaddropFormMobileCtaComponent />
            </div>
          )}
        </div>

        {/* ── Right: inline lead form (desktop) ────────────────────────────── */}
        {showLeadForm && isMobile === false && LeaddropFormComponent && (
          <RevealOnScroll
            x={400}
            scaleStart={0.5}
            enableAnimation={isAnimationEnabled}
          >
            <div
              className={cn(
                "flex shrink-0 flex-col overflow-hidden",
                "sm:w-[360px] md:w-[400px] lg:w-[420px] xl:w-[480px]",
              )}
            >
              {leaddropFormVariant === LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6 &&
              leadDropFormRow ? (
                <LeaddropFormComponent data={{ data: leadDropFormRow }} />
              ) : (
                <LeaddropFormComponent />
              )}
            </div>
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

export default Banner10;
